import { Injectable } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { mergeMap, retryWhen } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class RetryService {
  /**
   * Implements exponential backoff retry strategy for HTTP requests
   * @param maxRetries Maximum number of retry attempts
   * @param initialDelayMs Initial delay in milliseconds before first retry
   * @param backoffFactor Factor by which delay increases with each retry
   * @param maxDelayMs Maximum delay between retries in milliseconds
   * @param shouldRetry Optional function to determine if a specific error should be retried
   * @returns An RxJS operator that can be applied to an Observable
   */
  exponentialBackoff<T>(
    maxRetries: number = 3,
    initialDelayMs: number = 300,
    backoffFactor: number = 2,
    maxDelayMs: number = 10000,
    shouldRetry: (error: any) => boolean = () => true
  ) {
    return (source: Observable<T>) => {
      return source.pipe(
        retryWhen(errors => {
          return errors.pipe(
            mergeMap((error, i) => {
              const retryAttempt = i + 1;
              
              // If we've reached the max number of retries or the error shouldn't be retried, throw the error
              if (retryAttempt > maxRetries || !shouldRetry(error)) {
                return throwError(() => error);
              }
              
              // Calculate delay with exponential backoff formula, but cap it at maxDelayMs
              const delay = Math.min(
                initialDelayMs * Math.pow(backoffFactor, retryAttempt - 1),
                maxDelayMs
              );
              
              console.log(`[RETRY] Attempt ${retryAttempt}/${maxRetries} failed. Retrying in ${delay}ms...`);
              
              // Retry after calculated delay
              return timer(delay);
            })
          );
        })
      );
    };
  }

  /**
   * Wraps a promise-based function with exponential backoff retry logic
   * @param fn The async function to execute with retry logic
   * @param maxRetries Maximum number of retry attempts
   * @param initialDelayMs Initial delay in milliseconds before first retry
   * @param backoffFactor Factor by which delay increases with each retry
   * @param maxDelayMs Maximum delay between retries in milliseconds
   * @param shouldRetry Optional function to determine if a specific error should be retried
   * @returns A promise that resolves with the result of the function or rejects after all retries fail
   */
  async retryWithBackoff<T>(
    fn: () => Promise<T>,
    maxRetries: number = 3,
    initialDelayMs: number = 300,
    backoffFactor: number = 2,
    maxDelayMs: number = 10000,
    shouldRetry: (error: any) => boolean = () => true
  ): Promise<T> {
    let lastError: any;
    
    for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;
        
        // If this was the last attempt or we shouldn't retry this error, throw
        if (attempt > maxRetries || !shouldRetry(error)) {
          throw error;
        }
        
        // Calculate delay with exponential backoff formula, but cap it at maxDelayMs
        const delay = Math.min(
          initialDelayMs * Math.pow(backoffFactor, attempt - 1),
          maxDelayMs
        );
        
        console.log(`[RETRY] Attempt ${attempt}/${maxRetries + 1} failed. Retrying in ${delay}ms...`);
        
        // Wait for the calculated delay before next attempt
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    // This should never be reached due to the throw in the loop, but TypeScript needs it
    throw lastError;
  }
}